import { StandardTextFieldProps, TextField, useTheme } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { useCallback, useMemo, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { parseDate } from '../../utils/parseDate';
import { BoxFullWidth } from '../common/boxes';
import { Dayjs } from 'dayjs';

interface IAppDatePickerProps extends Omit<StandardTextFieldProps, 'variant'> {
  dateValue?: string,
  handleChange: (date: string) => void
}

// IMPORTANT! Designed to be used on filtering inputs
// This date picker is not reactive and dateValue prop is populated only at the beginning. 
// Any change to the dateValue prop after that will not affect the value of the date picker.
export const AppDatePicker = ({ dateValue, handleChange, ...props }: IAppDatePickerProps) => {
  const [value, setValue] = useState(dateValue);

  const onChange = useCallback((val: Dayjs | null) => {
    if (!val) {return;}
    setValue(parseDate(val));
    if (val.isValid()) {
      handleChange(parseDate(val));
    }
  }, [handleChange]);

  const onBlur = useCallback(() => {
    setValue(dateValue);
  }, [dateValue]);

  const validValue = useMemo(() => value || dateValue, [value, dateValue]);

  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <BoxFullWidth display={{ mobile: 'initial', tablet: 'none' }}>
        <MobileDatePicker
          disabled={props.disabled}
          onChange={onChange}
          value={validValue || null}
          inputFormat={'DD/MM/YYYY'}
          renderInput={(params) =>
            <TextField onBlur={onBlur} {...params} variant={'standard'} {...props}
                       sx={{ minWidth: 120, maxWidth: 170, ...props.sx }}/>}
        />
      </BoxFullWidth>
      <BoxFullWidth display={{ mobile: 'none', tablet: 'initial' }}>
        <DesktopDatePicker
          disabled={props.disabled}
          onChange={onChange}
          value={validValue || null}
          inputFormat={'DD/MM/YYYY'}
          PaperProps={{
            sx: {
              border: 1,
              borderColor: theme.palette.primary.main,
              borderRadius: theme.shape.borderRadius,
              '& .Mui-selected': {
                color: `${theme.palette.background.default} !important`,
                backgroundColor: `${theme.palette.primary.main} !important`
              }
            }
          }}
          renderInput={(params) =>
            <TextField {...params} onBlur={onBlur} variant={'standard'} {...props}
                       sx={{ minWidth: 120, maxWidth: 170, ...props.sx }}/>}
        />
      </BoxFullWidth>

    </LocalizationProvider>
  );
};